import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff, Lock, Mail, ShieldCheck, ArrowLeft } from 'lucide-react';
import { Spinner } from '../components/ui';
import { startGoogleSignIn } from '../services/api';
import logo from '../assets/images/haythix-logo.png';
import PublicNav from '../components/PublicNav';
import Seo from '../components/Seo';

// Inline Google "G" SVG — official multi-color brand mark.
function GoogleIcon({ className = 'w-5 h-5' }) {
  return (
    <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
  );
}

export default function LoginPage() {
  const [step, setStep] = useState('credentials'); // 'credentials' | 'mfa'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [googleError, setGoogleError] = useState(null);
  const { login, verifyMfa, isLoading, error } = useAuth();
  const navigate = useNavigate();

  const handleGoogle = async () => {
    setGoogleError(null); setGoogleBusy(true);
    try { await startGoogleSignIn('/'); }
    catch (e) { setGoogleError(e.message || 'Google sign-in failed'); setGoogleBusy(false); }
  };

  const handleCredentials = async (e) => {
    e.preventDefault();
    const result = await login({ email, password });
    if (result?.mfaPending) {
      setMfaToken(result.mfaToken);
      setStep('mfa');
    } else if (result?.success) {
      navigate('/');
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    const result = await verifyMfa({ mfaToken, code: mfaCode });
    if (result?.success) navigate('/');
  };

  return (
    <div className="min-h-screen bg-dark-900 flex flex-col">
      <Seo title="Sign in" description="Sign in to your haythix account." path="/login" noIndex />
      <PublicNav />
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-gradient-radial from-accent-primary/5 to-transparent rounded-full" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-gradient-radial from-accent-secondary/5 to-transparent rounded-full" />
      </div>

      <div className="relative flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-dark-700 border border-dark-500 mb-4 shadow-glow-gold">
            <img src={logo} alt="haythix" className="w-14 h-14" />
          </div>
          <h1 className="text-2xl font-bold text-accent-primary">haythix</h1>
          <p className="text-gray-400 mt-1">Enterprise Trading Platform</p>
        </div>

        {/* Step 1: Credentials */}
        {step === 'credentials' && (
          <div className="card border-dark-500">
            {/* Google OAuth — additive, not replacing email/password */}
            <button
              type="button"
              onClick={handleGoogle}
              disabled={googleBusy}
              className="w-full flex items-center justify-center gap-3 bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-60 disabled:cursor-not-allowed font-medium text-sm py-2.5 rounded-lg border border-gray-200 transition-colors"
            >
              {googleBusy ? <Spinner size="sm" /> : <GoogleIcon className="w-5 h-5" />}
              <span>{googleBusy ? 'Redirecting…' : 'Continue with Google'}</span>
            </button>
            {googleError && (
              <p className="mt-2 text-xs text-red-400 text-center">{googleError}</p>
            )}

            <div className="flex items-center gap-3 my-5">
              <div className="flex-1 h-px bg-dark-600" />
              <span className="text-[10px] uppercase tracking-widest text-gray-500">or sign in with email</span>
              <div className="flex-1 h-px bg-dark-600" />
            </div>

            <form onSubmit={handleCredentials} className="space-y-5">
              <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="input pl-10"
                      placeholder="you@example.com"
                      required
                      autoFocus
                    />
                  </div>
                </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="text-sm font-medium text-gray-300">Password</label>
                  <Link to="/forgot-password" className="text-xs text-violet-400 hover:text-violet-300 transition-colors">Forgot password?</Link>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="input pl-10 pr-10"
                    placeholder="Enter your password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-500 hover:text-gray-300"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={isLoading || !password || !email}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? <><Spinner size="sm" />Signing in…</> : 'Sign In'}
              </button>

            </form>
          </div>
        )}

        {/* Step 2: MFA */}
        {step === 'mfa' && (
          <div className="card border-dark-500">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-accent-primary/10 border border-accent-primary/20 flex items-center justify-center flex-shrink-0">
                <ShieldCheck className="w-5 h-5 text-accent-primary" />
              </div>
              <div>
                <h2 className="text-white font-semibold">Two-Factor Authentication</h2>
                <p className="text-gray-400 text-xs mt-0.5">Enter the 6-digit code from your authenticator app</p>
              </div>
            </div>

            <form onSubmit={handleMfa} className="space-y-5">
              <div>
                <label htmlFor="mfaCode" className="block text-sm font-medium text-gray-300 mb-2">Authenticator Code</label>
                <input
                  id="mfaCode"
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="input text-center text-2xl tracking-[0.5em] font-mono"
                  placeholder="000000"
                  autoFocus
                  required
                />
              </div>

              {error && (
                <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
              )}

              <button
                type="submit"
                disabled={isLoading || mfaCode.length !== 6}
                className="btn-primary w-full flex items-center justify-center gap-2"
              >
                {isLoading ? <><Spinner size="sm" />Verifying…</> : 'Verify & Sign In'}
              </button>

              <button
                type="button"
                onClick={() => { setStep('credentials'); setMfaCode(''); setMfaToken(''); }}
                className="w-full flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                <ArrowLeft className="w-3.5 h-3.5" /> Back to login
              </button>
            </form>
          </div>
        )}

        {/* Sign Up CTA — only shown on credentials step */}
        {step === 'credentials' && (
          <div className="mt-5 p-4 rounded-xl bg-dark-800 border border-dark-600 text-center">
            <p className="text-gray-400 text-sm mb-3">New to haythix?</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/" className="text-sm text-gray-300 hover:text-white border border-dark-500 hover:border-dark-400 px-4 py-2 rounded-lg transition-colors">
                View Pricing
              </Link>
              <Link to="/register" className="btn-primary text-sm px-4 py-2">
                Create Account
              </Link>
            </div>
          </div>
        )}

        <div className="text-center mt-6">
          <div className="flex items-center justify-center gap-4 text-xs text-gray-600">
            <Link to="/terms" className="hover:text-accent-primary transition-colors">Terms</Link>
            <span>•</span>
            <Link to="/privacy" className="hover:text-accent-primary transition-colors">Privacy</Link>
            <span>•</span>
            <Link to="/pricing" className="hover:text-accent-primary transition-colors">Pricing</Link>
          </div>
          <p className="text-gray-600 text-xs mt-3">© {new Date().getFullYear()} Borak Solutions LLC</p>
        </div>
        </div>
      </div>
    </div>
  );
}
